import { useEffect, useState } from "react";
import { useAppTranslate } from "src/hooks/useAppTranslate";

/** Subset of the (non-standard) Network Information API we rely on. */
type NetworkInformation = {
  downlink: number;
  effectiveType: string;
  addEventListener: (type: "change", listener: () => void) => void;
  removeEventListener: (type: "change", listener: () => void) => void;
};

export const useConnectionSpeed = () => {
  const [connectionMessage, setConnectionMessage] = useState(
    "Checking connection..."
  );
  const [isSlow, setIsSlow] = useState(false);
  const [downlink, setDownlink] = useState<number | null>(null); // To store downlink speed
  const [connectionType, setConnectionType] = useState<string | null>(null); // To store connection type (effectiveType)

  useEffect(() => {
    if (typeof window !== "undefined" && "connection" in navigator) {
      const nav = navigator as Navigator & {
        connection?: NetworkInformation;
        mozConnection?: NetworkInformation;
        webkitConnection?: NetworkInformation;
      };
      const connection = (nav.connection ||
        nav.mozConnection ||
        nav.webkitConnection) as NetworkInformation;

      const checkConnectionSpeed = () => {
        // Store the current downlink and connection type (e.g., 2g, 3g, 4g, 5g)
        const currentDownlink = connection.downlink;
        const currentConnectionType = connection.effectiveType;
        setDownlink(currentDownlink); // Update downlink state
        setConnectionType(currentConnectionType); // Update connection type state

        // Basic check for slow or fast connection based on downlink and effectiveType
        if (currentConnectionType === "2g" || currentDownlink < 1) {
          setConnectionMessage("Slow connection");
          setIsSlow(true);
        } else if (currentConnectionType === "3g" || currentDownlink < 3) {
          setIsSlow(true);
          setConnectionMessage("Medium connection");
        } else {
          setIsSlow(false);
          setConnectionMessage("Fast connection");
        }
      };

      // Initial check
      checkConnectionSpeed();

      // Listen for connection changes
      connection.addEventListener("change", checkConnectionSpeed);

      return () => {
        connection.removeEventListener("change", checkConnectionSpeed);
      };
    } else {
      setConnectionMessage("Connection speed check not supported");
      setIsSlow(false);
      setDownlink(null); // Reset downlink if unsupported
      setConnectionType(null); // Reset connection type if unsupported
    }
  }, []);

  return { message: connectionMessage, isSlow, downlink, connectionType }; // Return connection type and downlink
};

/**
 * The warning shown while photos upload. It used to render the hook's English
 * debug message ("Slow connection") in every market; the copy lives in
 * connection.slow / connection.medium.
 */
const ConnectionSpeedComponent = () => {
  const { isSlow, downlink, connectionType } = useConnectionSpeed();
  const translate = useAppTranslate();
  if (!isSlow) return null;
  const level =
    connectionType === "2g" || (downlink !== null && downlink < 1)
      ? "slow"
      : "medium";
  return <h5>{translate(`connection.${level}`)}</h5>;
};

export default ConnectionSpeedComponent;
